import { useMemo } from 'react';
import {
  addDays,
  endOfMonth,
  format,
  isAfter,
  isBefore,
  isSameDay,
  startOfMonth,
  subMonths,
} from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  TrendingUp,
  TrendingDown,
  Wallet,
  ArrowUpCircle,
  ArrowDownCircle,
  Loader2,
  CalendarRange,
} from 'lucide-react';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RTooltip,
  ReferenceLine,
} from 'recharts';
import { useAuth } from '@/app/providers/AuthProvider';
import { Card, CardContent } from '@/components/ui/card';
import { useCurrentClient } from '@/hooks/useCurrentClient';
import { useFinance } from '@/contexts/FinanceContext';

const fmtBrl = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const fmtBrlShort = (v: number) => {
  if (Math.abs(v) >= 1000) {
    return `R$ ${(v / 1000).toFixed(1).replace('.', ',')}k`;
  }
  return fmtBrl(v);
};

interface ProjectionPoint {
  day: string;
  date: Date;
  saldo: number;
}

export function PortalDashboardPage() {
  const { profile } = useAuth();
  const { client, clientId, loading: clientLoading } = useCurrentClient();
  const { accounts, transactions, isLoading } = useFinance();

  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const totalBalance = useMemo(
    () => accounts.filter((a) => a.isActive).reduce((s, a) => s + Number(a.currentBalance), 0),
    [accounts],
  );

  const upcoming = useMemo(() => {
    const horizon = addDays(today, 7);
    let toPay = 0;
    let toReceive = 0;
    let toPayCount = 0;
    let toReceiveCount = 0;
    for (const t of transactions) {
      if (t.status !== 'pending') continue;
      const d = t.dueDate;
      if (isBefore(d, today) || isAfter(d, horizon)) continue;
      if (t.type === 'expense') {
        toPay += Number(t.amount);
        toPayCount += 1;
      } else if (t.type === 'income') {
        toReceive += Number(t.amount);
        toReceiveCount += 1;
      }
    }
    return { toPay, toReceive, toPayCount, toReceiveCount };
  }, [transactions, today]);

  const monthSummary = useMemo(() => {
    const start = startOfMonth(today);
    const end = endOfMonth(today);
    const prevStart = startOfMonth(subMonths(today, 1));
    const prevEnd = endOfMonth(subMonths(today, 1));

    let income = 0;
    let expense = 0;
    let prevIncome = 0;
    let prevExpense = 0;

    for (const t of transactions) {
      if (t.status !== 'completed') continue;
      const ref = t.paymentDate ?? t.dueDate;
      if (!isBefore(ref, start) && !isAfter(ref, end)) {
        if (t.type === 'income') income += Number(t.amount);
        else if (t.type === 'expense') expense += Number(t.amount);
      } else if (!isBefore(ref, prevStart) && !isAfter(ref, prevEnd)) {
        if (t.type === 'income') prevIncome += Number(t.amount);
        else if (t.type === 'expense') prevExpense += Number(t.amount);
      }
    }
    const net = income - expense;
    const prevNet = prevIncome - prevExpense;
    const variation = prevNet === 0 ? null : ((net - prevNet) / Math.abs(prevNet)) * 100;
    return { income, expense, net, prevNet, variation };
  }, [transactions, today]);

  const projection = useMemo<ProjectionPoint[]>(() => {
    const points: ProjectionPoint[] = [];
    let saldo = totalBalance;

    // Próximos 30 dias: aplica deltas projetados (todos os pendings + completed do dia)
    for (let i = 0; i <= 30; i++) {
      const d = addDays(today, i);
      let dayDelta = 0;
      for (const t of transactions) {
        if (t.status !== 'pending') continue;
        if (!isSameDay(t.dueDate, d)) continue;
        if (t.type === 'income') dayDelta += Number(t.amount);
        else if (t.type === 'expense') dayDelta -= Number(t.amount);
      }
      saldo += dayDelta;
      points.push({
        day: format(d, 'dd/MM', { locale: ptBR }),
        date: d,
        saldo,
      });
    }
    return points;
  }, [transactions, totalBalance, today]);

  if (!clientId || clientLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center p-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  const monthLabel = format(today, "MMMM 'de' yyyy", { locale: ptBR });
  const variation = monthSummary.variation;

  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-brand text-2xl font-bold text-foreground">
          Olá, {profile?.name?.split(' ')[0] ?? 'Doutor'} 👋
        </h1>
        <p className="text-sm text-muted-foreground">
          {client?.name ? `${client.name} · ` : ''}visão executiva — {monthLabel}
        </p>
      </div>

      {isLoading ? (
        <Card>
          <CardContent className="flex items-center justify-center p-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Linha 1 — Saldos e fluxo curto */}
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <Card>
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                      Saldo total
                    </p>
                    <p className="mt-1 text-3xl font-bold tabular-nums text-foreground">
                      {fmtBrl(totalBalance)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      em {accounts.filter((a) => a.isActive).length} conta
                      {accounts.filter((a) => a.isActive).length === 1 ? '' : 's'}
                    </p>
                  </div>
                  <Wallet size={28} className="text-primary/60" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                      A receber · 7 dias
                    </p>
                    <p className="mt-1 text-3xl font-bold tabular-nums text-income">
                      {fmtBrl(upcoming.toReceive)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {upcoming.toReceiveCount} lançamento
                      {upcoming.toReceiveCount === 1 ? '' : 's'}
                    </p>
                  </div>
                  <ArrowUpCircle size={28} className="text-income/60" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                      A pagar · 7 dias
                    </p>
                    <p className="mt-1 text-3xl font-bold tabular-nums text-expense">
                      {fmtBrl(upcoming.toPay)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {upcoming.toPayCount} lançamento
                      {upcoming.toPayCount === 1 ? '' : 's'}
                    </p>
                  </div>
                  <ArrowDownCircle size={28} className="text-expense/60" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Linha 2 — Resultado do mês + comparativo */}
          <Card>
            <CardContent className="p-5">
              <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    Resultado de {monthLabel}
                  </p>
                  <p
                    className={`mt-1 text-3xl font-bold tabular-nums ${
                      monthSummary.net >= 0 ? 'text-income' : 'text-expense'
                    }`}
                  >
                    {fmtBrl(monthSummary.net)}
                  </p>
                  <div className="mt-1 flex flex-wrap gap-x-4 text-xs text-muted-foreground">
                    <span>
                      Receitas:{' '}
                      <span className="font-medium text-income">{fmtBrl(monthSummary.income)}</span>
                    </span>
                    <span>
                      Despesas:{' '}
                      <span className="font-medium text-expense">
                        {fmtBrl(monthSummary.expense)}
                      </span>
                    </span>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    vs. mês anterior
                  </p>
                  <p
                    className={`mt-1 inline-flex items-center gap-1 text-xl font-bold tabular-nums ${
                      variation === null
                        ? 'text-muted-foreground'
                        : variation >= 0
                          ? 'text-income'
                          : 'text-expense'
                    }`}
                  >
                    {variation === null ? (
                      '—'
                    ) : (
                      <>
                        {variation >= 0 ? <TrendingUp size={18} /> : <TrendingDown size={18} />}
                        {variation > 0 ? '+' : ''}
                        {variation.toFixed(1).replace('.', ',')}%
                      </>
                    )}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Anterior: <span className="tabular-nums">{fmtBrl(monthSummary.prevNet)}</span>
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Linha 3 — Projeção de saldo 30 dias */}
          <Card>
            <CardContent className="p-5">
              <div className="mb-4 flex items-center gap-2">
                <CalendarRange size={16} className="text-primary" />
                <h2 className="text-sm font-semibold text-foreground">
                  Projeção de saldo · próximos 30 dias
                </h2>
              </div>
              {projection.length === 0 ? (
                <p className="py-8 text-center text-sm text-muted-foreground">
                  Sem lançamentos pendentes para projetar.
                </p>
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <AreaChart data={projection} margin={{ top: 5, right: 10, bottom: 0, left: 0 }}>
                    <defs>
                      <linearGradient id="colorSaldo" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.4} />
                        <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis
                      dataKey="day"
                      tick={{ fontSize: 11 }}
                      interval="preserveStartEnd"
                      tickMargin={6}
                    />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={fmtBrlShort} width={70} />
                    <ReferenceLine
                      y={0}
                      stroke="hsl(var(--muted-foreground))"
                      strokeDasharray="3 3"
                    />
                    <RTooltip
                      formatter={(v) => fmtBrl(Number(v))}
                      labelFormatter={(label) => `Dia ${String(label)}`}
                      contentStyle={{
                        fontSize: 12,
                        borderRadius: 8,
                        border: '1px solid hsl(var(--border))',
                      }}
                    />
                    <Area
                      type="monotone"
                      dataKey="saldo"
                      stroke="hsl(var(--primary))"
                      strokeWidth={2}
                      fill="url(#colorSaldo)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              )}
              <p className="mt-3 text-xs text-muted-foreground">
                Considera saldo atual + lançamentos pendentes (a pagar e a receber) projetados pelas
                datas de vencimento.
              </p>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
